import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, formatINR, track } from "../lib/api";
import { useSEO } from "../lib/seo";
import { useStore } from "../lib/store";
import { Check, Package, ChevronRight } from "lucide-react";

export default function OrderConfirm() {
  const { orderNumber } = useParams();
  const [params] = useSearchParams();
  const { settings } = useStore();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);
  useSEO({ title: "Order confirmed — Haven Hives", description: "Your Haven Hives order is confirmed." });

  useEffect(() => {
    const qs = params.get("tx_id") ? `tx_id=${params.get("tx_id")}` : params.get("token") ? `token=${params.get("token")}` : "";
    api.get(`/orders/${orderNumber}/receipt?${qs}`).then((r) => {
      setOrder(r.data);
      const key = `hh_purchased_${r.data.order_number}`;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, "1");
        track("purchase", { value: r.data.total, currency: "INR", transaction_id: r.data.order_number,
          content_ids: (r.data.lines || []).map((l) => l.slug || l.variant_id), num_items: (r.data.lines || []).reduce((s, l) => s + (l.qty || 1), 0) });
      }
    }).catch(() => setError(true));
  }, [orderNumber, params]);

  if (error) return (
    <div className="max-w-xl mx-auto text-center py-24 px-5" data-testid="order-confirm-error">
      <h1 className="font-display text-3xl font-semibold">We couldn't open that receipt</h1>
      <p className="text-charcoal/70 mt-3">For your security, receipts need the secure link from checkout. Use Track Order with your order number and mobile number instead.</p>
      <Link to="/track-order" className="inline-block mt-6 bg-forest text-white hover:bg-forest-dark px-6 py-3 rounded-full">Track your order</Link>
    </div>
  );
  if (!order) return <div className="max-w-3xl mx-auto px-5 py-16" aria-busy="true"><div className="h-64 rounded-3xl bg-sage/20 animate-pulse" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12" data-testid="order-confirm-page">
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-sage flex items-center justify-center mx-auto animate-pop"><Check className="w-8 h-8 text-forest" /></div>
        <h1 className="font-display font-semibold text-3xl sm:text-4xl mt-5" data-testid="order-confirm-heading">
          {order.payment_method === "cod" ? "Order placed — yay!" : "Payment received — thank you!"}
        </h1>
        <p className="text-charcoal/70 mt-3">Order <span className="font-bold text-charcoal" data-testid="order-confirm-number">{order.order_number}</span> is confirmed.{" "}
          {settings?.integrations?.email_enabled
            ? <span data-testid="order-confirm-email-note">A confirmation email is on its way to <span className="font-semibold text-charcoal">{order.address?.email}</span>, and we'll email you when it's packed, shipped and delivered.</span>
            : "A confirmation would normally be emailed — email updates wait on a configured provider in this demonstration build."}</p>
      </div>

      <div className="bg-white rounded-3xl card-outline p-6 mt-8">
        <div className="flex items-center gap-2 text-sm font-semibold" data-testid="order-confirm-status">
          <Package className="w-4 h-4 text-forest" /> Status: {order.status.replace(/_/g, " ")}
        </div>
        <ul className="mt-5 divide-y divide-charcoal/10">
          {order.lines.map((l, i) => (
            <li key={i} className="flex gap-3 py-3 text-sm items-center">
              <img src={l.image} alt={l.name} width="52" height="52" className="w-13 h-13 w-[52px] h-[52px] rounded-xl object-cover" loading="lazy" />
              <div className="flex-1 min-w-0"><p className="font-semibold">{l.name}</p><p className="text-xs text-charcoal/60">{l.variant_name} × {l.qty}</p></div>
              <span className="font-semibold">{formatINR(l.price * l.qty)}</span>
            </li>
          ))}
        </ul>
        <dl className="mt-3 pt-3 border-t border-charcoal/10 text-sm space-y-1.5">
          <div className="flex justify-between"><dt className="text-charcoal/70">Subtotal</dt><dd>{formatINR(order.subtotal)}</dd></div>
          {order.discount > 0 && <div className="flex justify-between text-forest"><dt>Coupon {order.coupon}</dt><dd>− {formatINR(order.discount)}</dd></div>}
          <div className="flex justify-between"><dt className="text-charcoal/70">Shipping ({order.payment_method === "cod" ? "COD" : "prepaid"})</dt><dd>{order.shipping_amount === 0 ? "Free" : formatINR(order.shipping_amount)}</dd></div>
          <div className="flex justify-between font-bold text-base pt-2 border-t border-charcoal/10"><dt>Total {order.payment_method === "cod" ? "(pay on delivery)" : "paid"}</dt><dd data-testid="order-confirm-total">{formatINR(order.total)}</dd></div>
        </dl>
        <p className="text-xs text-charcoal/50 mt-2">{order.tax_wording}</p>
      </div>

      <div className="bg-white rounded-3xl card-outline p-6 mt-4 text-sm">
        <h2 className="font-display font-semibold text-lg">Delivering to</h2>
        <p className="text-charcoal/75 mt-2 leading-relaxed">{order.address.name}, {order.address.address1}{order.address.address2 ? `, ${order.address.address2}` : ""}, {order.address.city}, {order.address.state} — {order.address.pin}</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
        <Link to={`/track-order?order_number=${order.order_number}`} data-testid="order-confirm-track-link"
          className="bg-forest text-white font-semibold px-6 py-3.5 rounded-full text-center flex items-center justify-center gap-1">Track this order <ChevronRight className="w-4 h-4" /></Link>
        <Link to="/collections/shop-all" className="border border-charcoal/20 font-semibold px-6 py-3.5 rounded-full text-center">Keep shopping</Link>
      </div>
    </div>
  );
}
